import { useEffect, useRef, useCallback, useState } from "react";
import { io } from "socket.io-client";
import getCookie from "../Utils/getCookie";

const deriveSocketBase = (apiBase) => {
  try {
    const { protocol, host } = new URL(apiBase);
    return `${protocol}//${host}`;
  } catch {
    return (apiBase || "http://localhost:6001").replace(/\/+$/, "");
  }
};

const SOCKET_BASE = deriveSocketBase(
  process.env.REACT_APP_BASE_URL || "http://localhost:6001"
);

/**
 * Connects to the /remittance namespace and joins the admin subscription room.
 * Calls handlers when a remittance is created or its status changes.
 *
 * @param {object} handlers
 * @param {(remittance: object) => void} [handlers.onCreated]
 * @param {(remittance: object) => void} [handlers.onStatusUpdated]
 */
const useRemittanceSocket = (handlers = {}) => {
  const socketRef = useRef(null);
  const handlersRef = useRef(handlers);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    const token = getCookie("token-siraj");

    const socket = io(`${SOCKET_BASE}/remittance`, {
      auth: { token },
      transports: ["websocket"],
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setIsConnected(true);
      socket.emit("join_remittances_admin");
    });

    socket.on("disconnect", () => setIsConnected(false));

    socket.on("remittance_created", (data) => {
      handlersRef.current.onCreated?.(data?.remittance);
    });

    socket.on("remittance_status_updated", (data) => {
      handlersRef.current.onStatusUpdated?.(data?.remittance);
    });

    socket.on("error", (data) => {
      console.warn("[RemittanceSocket] error:", data?.message);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, []);

  const reconnect = useCallback(() => {
    socketRef.current?.connect();
  }, []);

  return { isConnected, reconnect };
};

export default useRemittanceSocket;
